import { EmailTemplate } from '@spike/email-marketing-model';
import clsx from 'clsx';
import { FunctionComponent, useState } from 'react';
import styles from './SelectedTemplatePreview.module.scss';

type PreviewDevice = 'desktop' | 'mobile';

interface SelectedTemplatePreviewProps {
    template: EmailTemplate;
    /**
     * Click handler for the "Edit" action link. When omitted the link is not
     * rendered — used by Step 3 (Review & Send) which shows the same preview
     * in read-only mode.
     */
    onEditTemplate?: () => void;
    /**
     * Label rendered inside the Edit action link. Defaults to `"Edit"`; the
     * wizard swaps it to `"Duplicate & Edit"` for system templates so the
     * user knows the click will create their own copy first.
     */
    editLabel?: string;
    /**
     * When true, the Edit action link is disabled and its label is replaced
     * with a short loading state (`Duplicating…`). Used while the clone-and-
     * edit flow is in flight.
     */
    editDisabled?: boolean;
    /**
     * Click handler for the "Change Template" action link. Same opt-in
     * semantics as `onEditTemplate`.
     */
    onChangeTemplate?: () => void;
}

/**
 * Right column content shown once a template has been picked. Replaces the
 * Email Saved / Template Gallery tab bar.
 *
 * The iframe renders the template's stored HTML at the device-appropriate
 * width (desktop = 600px, mobile = 375px) with full-size rendering — no
 * thumbnail scaling. The user can verify how the email will look before
 * locking it into the campaign.
 *
 *   Edit            → opens the editor (the wizard handles "embedded mode" so
 *                     the editor overlays on top and returns here on save).
 *   Change Template → clears the selection in the wizard; the tab bar comes
 *                     back into view and the user can pick a different one.
 *
 * When either handler is omitted the corresponding link is hidden — this lets
 * Step 3 reuse the component in read-only "review" mode.
 */
const SelectedTemplatePreview: FunctionComponent<SelectedTemplatePreviewProps> = ({
    template,
    onEditTemplate,
    editLabel = 'Edit',
    editDisabled = false,
    onChangeTemplate
}) => {
    const [device, setDevice] = useState<PreviewDevice>('desktop');

    return (
        <div className={styles.root}>
            <div className={styles.toolbar}>
                <div className={styles.deviceToggle}>
                    <button
                        type="button"
                        className={clsx(styles.deviceButton, {
                            [styles.deviceButtonActive]: device === 'desktop'
                        })}
                        onClick={() => setDevice('desktop')}
                    >
                        Desktop
                    </button>
                    <button
                        type="button"
                        className={clsx(styles.deviceButton, {
                            [styles.deviceButtonActive]: device === 'mobile'
                        })}
                        onClick={() => setDevice('mobile')}
                    >
                        Mobile
                    </button>
                </div>

                {(onEditTemplate || onChangeTemplate) && (
                    <div className={styles.actionLinks}>
                        {onEditTemplate && (
                            <button
                                type="button"
                                className={styles.actionLink}
                                onClick={onEditTemplate}
                                disabled={editDisabled}
                            >
                                {editDisabled ? 'Duplicating…' : editLabel}
                            </button>
                        )}
                        {onChangeTemplate && (
                            <button
                                type="button"
                                className={styles.actionLink}
                                onClick={onChangeTemplate}
                            >
                                Change Template
                            </button>
                        )}
                    </div>
                )}
            </div>

            <div className={styles.previewScroll}>
                <div className={clsx(styles.previewFrame, styles[`previewFrame_${device}`])}>
                    {template.html ? (
                        <iframe
                            className={styles.iframe}
                            title={template.name}
                            srcDoc={template.html}
                            sandbox=""
                        />
                    ) : (
                        <div className={styles.placeholder}>This template has no content yet.</div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SelectedTemplatePreview;
